import React, { useState, useEffect } from 'react';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import ExcelJS from 'exceljs';
import apiClient from '../utils/apiClient';
import ConfirmationModal from './ConfirmationModal';
import {
  DocumentArrowDownIcon,
  XMarkIcon,
  DocumentTextIcon,
  TableCellsIcon,
  CheckCircleIcon,
  XCircleIcon
} from '@heroicons/react/24/outline';

// Alternative saveAs Implementierung falls file-saver nicht funktioniert
const saveAs = (blob: Blob, filename: string) => {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

interface Member {
  mg_nr: number;
  mitglied_nr: string;
  name: string;
  vorname: string;
  email: string;
  rolle?: string;
  primary_role?: {
    id: number;
    name: string;
    description: string;
    priority: number;
    created_at: string;
  };
}

interface DrinkColumn {
  id: number;
  name: string;
  price: number;
  active: boolean;
  sort_order: number;
}

interface DrinkListExportProps {
  isOpen: boolean;
  onClose: () => void;
}

const DrinkListExport: React.FC<DrinkListExportProps> = ({ isOpen, onClose }) => {
  const [members, setMembers] = useState<Member[]>([]);
  const [loading, setLoading] = useState(false);
  const [month, setMonth] = useState('');
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [pdfBlob, setPdfBlob] = useState<Blob | null>(null);
  const [columns, setColumns] = useState<DrinkColumn[]>([]);
  const [allColumns, setAllColumns] = useState<DrinkColumn[]>([]);
  const [isManageColumnsOpen, setIsManageColumnsOpen] = useState(false);
  const [editingColumn, setEditingColumn] = useState<DrinkColumn | null>(null);
  const [newColumnName, setNewColumnName] = useState('');
  const [newColumnPrice, setNewColumnPrice] = useState('');
  const [includeGuests, setIncludeGuests] = useState(false);
  const [confirmModal, setConfirmModal] = useState({
    isOpen: false,
    type: 'success' as 'success' | 'error',
    title: '',
    message: '',
    variant: 'single' as 'single',
  });

  useEffect(() => {
    if (isOpen) {
      fetchMembers();
      fetchColumns();
      fetchAllColumns();
      // Setze aktuellen Monat als Standard
      const now = new Date();
      const monthNames = ['Januar', 'Februar', 'März', 'April', 'Mai', 'Juni',
                         'Juli', 'August', 'September', 'Oktober', 'November', 'Dezember'];
      setMonth(`${monthNames[now.getMonth()]} ${now.getFullYear()}`);
    }
  }, [isOpen]);

  const fetchMembers = async () => {
    setLoading(true);
    try {
      const response = await apiClient.get('/api/members');
      const sortedData = response.data.sort((a: Member, b: Member) => {
        const vornameA = (a.vorname || '').toLowerCase();
        const vornameB = (b.vorname || '').toLowerCase();
        if (vornameA < vornameB) return -1;
        if (vornameA > vornameB) return 1;
        const nameA = (a.name || '').toLowerCase();
        const nameB = (b.name || '').toLowerCase();
        return nameA.localeCompare(nameB);
      });
      setMembers(sortedData);
    } catch (err) {
      console.error('Fehler beim Laden der Mitglieder:', err);
      setConfirmModal({
        isOpen: true,
        type: 'error',
        title: 'Fehler',
        message: 'Mitglieder konnten nicht geladen werden.',
        variant: 'single'
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchColumns = async () => {
    try {
      const response = await apiClient.get('/api/drink-list-columns/active');
      // Konvertiere price zu Number
      const columnsData = response.data.map((col: any) => ({
        ...col,
        price: parseFloat(col.price)
      }));
      setColumns(columnsData);
    } catch (err) {
      console.error('Fehler beim Laden der Spalten:', err);
      setConfirmModal({
        isOpen: true,
        type: 'error',
        title: 'Fehler',
        message: 'Spaltenkonfiguration konnte nicht geladen werden.',
        variant: 'single'
      });
    }
  };

  const fetchAllColumns = async () => {
    try {
      const response = await apiClient.get('/api/drink-list-columns');
      // Konvertiere price zu Number
      const columnsData = response.data.map((col: any) => ({
        ...col,
        price: parseFloat(col.price)
      }));
      setAllColumns(columnsData);
    } catch (err) {
      console.error('Fehler beim Laden aller Spalten:', err);
    }
  };

  // Trenne Mitglieder und Gäste
  const regularMembers = members.filter((member) => {
    return member.primary_role?.name !== 'guest' && member.rolle !== 'Gast';
  });

  const guestMembers = members.filter((member) => {
    return member.primary_role?.name === 'guest' || member.rolle === 'Gast';
  });

  // Filtere basierend auf includeGuests
  const filteredMembers = includeGuests ? members : regularMembers;
  const guestCount = guestMembers.length;

  const generatePDF = async (forPreview = false) => {
    const pdf = new jsPDF('p', 'mm', 'a4');
    const pageWidth = 210;
    const pageHeight = 297;
    const margin = 10;
    const contentWidth = 190;
    const cellHeight = 6;
    const headerHeight = 25;
    const footerHeight = 12;

    // Dynamische Spaltenbreiten-Berechnung
    const nameColumnWidth = contentWidth * 0.25; // Name bekommt 25% der Breite
    const remainingWidth = contentWidth - nameColumnWidth;
    const dataColumns = columns.length + 1; // +1 für Gesamt-Spalte
    const dataColumnWidth = remainingWidth / dataColumns;

    // Tabellendaten vorbereiten - mit Mitgliedern
    const tableData: any[] = [];

    // Reguläre Mitglieder
    regularMembers.forEach((member) => {
      const fullName = `${member.vorname || '-'} ${member.name || '-'}`;
      const truncatedName = fullName.length > 25 ? `${fullName.slice(0, 23)}..` : fullName;
      const row: any[] = [
        {
          content: truncatedName,
          styles: {
            cellPadding: { top: 1, right: 0, bottom: 1, left: 2 },
            fontSize: 11,
            textColor: [17, 24, 39],
            minCellHeight: cellHeight,
            maxCellHeight: cellHeight,
          },
        }
      ];

      // Füge leere Zellen für jede Spalte hinzu
      columns.forEach(() => row.push(''));
      row.push('');
      tableData.push(row);
    });

    // Gäste-Bereich (nur wenn includeGuests aktiv ist und Gäste vorhanden)
    if (includeGuests && guestMembers.length > 0) {
      // Separator-Zeile
      const separatorRow: any[] = [
        {
          content: 'GÄSTE',
          colSpan: columns.length + 2,
          styles: {
            fillColor: [249, 115, 22], // Orange
            textColor: [255, 255, 255],
            fontStyle: 'bold',
            halign: 'center',
            fontSize: 11,
            cellPadding: { top: 3, bottom: 3, left: 2, right: 2 },
          },
        }
      ];
      tableData.push(separatorRow);

      // Gäste hinzufügen
      guestMembers.forEach((member) => {
        const fullName = `${member.vorname || '-'} ${member.name || '-'}`;
        const truncatedName = fullName.length > 25 ? `${fullName.slice(0, 23)}..` : fullName;
        const row: any[] = [
          {
            content: truncatedName,
            styles: {
              cellPadding: { top: 1, right: 0, bottom: 1, left: 2 },
              fontSize: 11,
              textColor: [17, 24, 39],
              minCellHeight: cellHeight,
              maxCellHeight: cellHeight,
              fillColor: [255, 247, 237], // Sehr helles Orange
            },
          }
        ];

        columns.forEach(() => row.push({ content: '', styles: { fillColor: [255, 247, 237] } }));
        row.push({ content: '', styles: { fillColor: [255, 247, 237] } });
        tableData.push(row);
      });
    }

    let currentPage = 1;
    let totalPages = 1;

    const addHeader = (pageNumber: number) => {
      // Grüner Header-Balken
      pdf.setFillColor(34, 197, 94); // Tailwind success-500
      pdf.rect(margin, margin, contentWidth, 20, 'F');
      
      // Vereinsname
      pdf.setFont('helvetica', 'bold');
      pdf.setFontSize(16);
      pdf.setTextColor(255, 255, 255);
      pdf.text('HSV Berlin-Adlershof e.V.', margin + 5, margin + 12);
      
      // Dokumenttitel
      pdf.setFont('helvetica', 'normal');
      pdf.setFontSize(12);
      pdf.text('Getränkeliste', margin + 5, margin + 18);
      
      // Monat
      pdf.setFontSize(14);
      pdf.setFont('helvetica', 'bold');
      pdf.text(month || 'Monat', pageWidth - margin - 5, margin + 12, { align: 'right' });
      
      // Seitenzahl
      pdf.setFontSize(10);
      pdf.setFont('helvetica', 'normal');
      pdf.text(`Seite ${pageNumber} von ${totalPages}`, pageWidth - margin - 5, margin + 18, { align: 'right' });
    };

    const addFooter = () => {
      pdf.setLineWidth(0.3);
      pdf.setDrawColor(34, 197, 94);
      pdf.line(margin, pageHeight - margin - 5, pageWidth - margin, pageHeight - margin - 5);
      
      pdf.setFont('helvetica', 'italic');
      pdf.setFontSize(8);
      pdf.setTextColor(75, 85, 99);
      
      const footerText = 'Vielen Dank für Ihren Besuch! – HSV Berlin-Adlershof e.V.';
      pdf.text(footerText, pageWidth / 2, pageHeight - margin, { align: 'center' });
    };

    // Dynamische Header-Zeile erstellen
    const headerRow = ['Name'];
    columns.forEach(col => {
      headerRow.push(`${col.name}\n${col.price.toFixed(2)} €`);
    });
    headerRow.push('Gesamt');

    // Dynamische Spaltenbreiten für jede Spalte
    const columnStyles: Record<number, any> = {
      0: { halign: 'left', cellWidth: nameColumnWidth },
    };

    // Alle Daten-Spalten (inkl. Gesamt) bekommen die gleiche Breite
    const totalDataColumns = columns.length + 1;
    for (let i = 1; i <= totalDataColumns; i++) {
      columnStyles[i] = { cellWidth: dataColumnWidth };
    }

    // Tabelle generieren
    autoTable(pdf, {
      startY: margin + headerHeight,
      head: [headerRow],
      body: tableData,
      theme: 'grid',
      styles: {
        font: 'helvetica',
        fontSize: 10,
        textColor: [17, 24, 39],
        lineColor: [209, 213, 219],
        lineWidth: 0.25,
        cellPadding: { top: 1, bottom: 1, left: 1, right: 1 },
        minCellHeight: cellHeight,
        maxCellHeight: cellHeight,
        halign: 'center',
        valign: 'middle',
      },
      headStyles: {
        fillColor: [243, 244, 246],
        textColor: [17, 24, 39],
        fontStyle: 'bold',
        fontSize: 10,
        cellPadding: { top: 2, bottom: 2, left: 1, right: 1 },
        minCellHeight: 10,
        maxCellHeight: 10,
      },
      columnStyles,
      alternateRowStyles: {
        fillColor: [249, 250, 251],
      },
      margin: { top: margin + headerHeight, bottom: footerHeight, left: margin },
      didDrawPage: (data: any) => {
        totalPages = (pdf as any).internal.getNumberOfPages();
        addHeader(currentPage);
        addFooter();
        currentPage++;
      },
    });

    // Header und Footer für alle Seiten nochmal setzen
    currentPage = 1;
    for (let i = 1; i <= totalPages; i++) {
      (pdf as any).setPage(i);
      addHeader(currentPage);
      addFooter();
      currentPage++;
    }

    if (forPreview) {
      const pdfBlob = pdf.output('blob');
      setPdfBlob(pdfBlob);
      
      // Für die Vorschau zeigen wir einfach die HTML-Tabelle
      // Das PDF wird im Hintergrund für den Download bereitgehalten
      setIsPreviewOpen(true);
      return;
    } else {
      pdf.save(`Getraenkeliste_${month?.replace(' ', '_') || 'Export'}.pdf`);
      setConfirmModal({
        isOpen: true,
        type: 'success',
        title: 'Export erfolgreich',
        message: 'Die PDF-Datei wurde erfolgreich heruntergeladen.',
        variant: 'single'
      });
    }
  };

  const generateExcel = async () => {
    try {
      const workbook = new ExcelJS.Workbook();
      workbook.creator = 'HSV Berlin-Adlershof e.V.';
      workbook.created = new Date();
      workbook.modified = new Date();

      const worksheet = workbook.addWorksheet('Getränkeliste', {
        pageSetup: {
          paperSize: 9, // A4
          orientation: 'portrait',
          margins: {
            left: 0.1,
            right: 0.05,
            top: 0.1,
            bottom: 0.1,
            header: 0,
            footer: 0,
          },
        },
      });

      // Header mit Vereinsname und Monat
      worksheet.addRow(['Getränkeliste', '', 'HSV Berlin-Adlershof e.V.', month || 'Monat', '', '', '']);
      worksheet.getRow(1).font = { name: 'Arial', size: 16, bold: true, color: { argb: 'FFFFFFFF' } };
      worksheet.getRow(1).fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: 'FF22C55E' }, // Grün
      };
      worksheet.getRow(1).alignment = { vertical: 'middle', horizontal: 'left' };
      worksheet.getCell('D1').alignment = { vertical: 'middle', horizontal: 'right' };
      worksheet.getRow(1).height = 30;

      // Vereinsinfo
      worksheet.addRow(['HSV Berlin-Adlershof e.V.', '', '', '', '', '', '']);
      worksheet.getRow(2).font = { name: 'Arial', size: 12 };
      worksheet.getRow(2).alignment = { vertical: 'middle', horizontal: 'left' };
      worksheet.getRow(2).height = 25;

      // Leerzeile
      worksheet.addRow([]);

      // Spaltenüberschriften - dynamisch
      const headers = ['Name'];
      columns.forEach(col => {
        headers.push(`${col.name}\n${col.price.toFixed(2)} €`);
      });
      headers.push('Gesamt');

      worksheet.addRow(headers);
      const headerRow = worksheet.getRow(4);
      headerRow.font = { name: 'Arial', size: 11, bold: true };
      headerRow.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFF3F4F6' } };
      headerRow.alignment = { vertical: 'middle', horizontal: 'center', wrapText: true };
      headerRow.height = 35;

      // Spaltenbreiten - dynamisch
      const columnWidths = [{ width: 25 }]; // Name
      columns.forEach(() => columnWidths.push({ width: 12 }));
      columnWidths.push({ width: 12 }); // Gesamt
      worksheet.columns = columnWidths;

      // Mitgliederdaten hinzufügen - Reguläre Mitglieder
      regularMembers.forEach((member, index) => {
        const fullName = `${member.vorname || '-'} ${member.name || '-'}`;
        const rowData = [fullName];
        columns.forEach(() => rowData.push(''));
        rowData.push('');

        const row = worksheet.addRow(rowData);
        row.font = { name: 'Arial', size: 10 };
        row.alignment = { vertical: 'middle', horizontal: 'center' };
        row.getCell(1).alignment = { horizontal: 'left' };
        row.height = 20;

        if (index % 2 === 0) {
          row.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFF9FAFB' } };
        }

        row.eachCell((cell) => {
          cell.border = {
            top: { style: 'thin', color: { argb: 'FFD1D5DB' } },
            left: { style: 'thin', color: { argb: 'FFD1D5DB' } },
            bottom: { style: 'thin', color: { argb: 'FFD1D5DB' } },
            right: { style: 'thin', color: { argb: 'FFD1D5DB' } },
          };
        });
      });

      // Gäste-Bereich hinzufügen
      if (includeGuests && guestMembers.length > 0) {
        // Separator-Zeile
        const separatorData = ['GÄSTE'];
        columns.forEach(() => separatorData.push(''));
        separatorData.push('');

        const separatorRow = worksheet.addRow(separatorData);
        separatorRow.font = { name: 'Arial', size: 11, bold: true, color: { argb: 'FFFFFFFF' } };
        separatorRow.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFF97316' } }; // Orange
        separatorRow.alignment = { vertical: 'middle', horizontal: 'center' };
        separatorRow.height = 25;

        // Merge separator cells
        const startCol = 'A';
        const endCol = String.fromCharCode(65 + columns.length + 1);
        const rowNum = separatorRow.number;
        worksheet.mergeCells(`${startCol}${rowNum}:${endCol}${rowNum}`);

        // Gäste hinzufügen
        guestMembers.forEach((member, index) => {
          const fullName = `${member.vorname || '-'} ${member.name || '-'}`;
          const rowData = [fullName];
          columns.forEach(() => rowData.push(''));
          rowData.push('');

          const row = worksheet.addRow(rowData);
          row.font = { name: 'Arial', size: 10 };
          row.alignment = { vertical: 'middle', horizontal: 'center' };
          row.getCell(1).alignment = { horizontal: 'left' };
          row.height = 20;

          // Helles Orange für Gäste
          row.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFFF7ED' } };

          row.eachCell((cell) => {
            cell.border = {
              top: { style: 'thin', color: { argb: 'FFD1D5DB' } },
              left: { style: 'thin', color: { argb: 'FFD1D5DB' } },
              bottom: { style: 'thin', color: { argb: 'FFD1D5DB' } },
              right: { style: 'thin', color: { argb: 'FFD1D5DB' } },
            };
          });
        });
      }

      // Footer
      worksheet.addRow([]);
      const footerRow = worksheet.addRow(['', '', '', 'Vielen Dank für Ihren Besuch!', '', '', '']);
      footerRow.font = { name: 'Arial', size: 8, italic: true, color: { argb: 'FF6B7280' } };
      footerRow.alignment = { vertical: 'middle', horizontal: 'center' };
      footerRow.height = 20;

      // Zellen verbinden - dynamisch
      worksheet.mergeCells('A1:C1');
      const lastCol = String.fromCharCode(65 + columns.length + 1);
      worksheet.mergeCells(`D1:${lastCol}1`);
      worksheet.mergeCells('A2:C2');

      // Berechne Footer-Position (abhängig davon ob Gäste enthalten sind)
      const totalRows = regularMembers.length + (includeGuests && guestMembers.length > 0 ? guestMembers.length + 1 : 0);
      const footerEndCol = String.fromCharCode(65 + columns.length);
      worksheet.mergeCells(`D${totalRows + 6}:${footerEndCol}${totalRows + 6}`);

      // Datei generieren und herunterladen
      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], { 
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
      });
      saveAs(blob, `Getraenkeliste_${month?.replace(' ', '_') || 'Export'}.xlsx`);
      
      setConfirmModal({
        isOpen: true,
        type: 'success',
        title: 'Export erfolgreich',
        message: 'Die Excel-Datei wurde erfolgreich heruntergeladen.',
        variant: 'single'
      });
    } catch (error) {
      console.error('Fehler beim Excel-Export:', error);
      setConfirmModal({
        isOpen: true,
        type: 'error',
        title: 'Export fehlgeschlagen',
        message: 'Die Excel-Datei konnte nicht erstellt werden.',
        variant: 'single'
      });
    }
  };

  const handlePreview = () => {
    generatePDF(true);
  };

  const closePreview = () => {
    setIsPreviewOpen(false);
    setPdfBlob(null);
  };
  
  const downloadPdfFromPreview = () => {
    if (pdfBlob) {
      const url = URL.createObjectURL(pdfBlob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `Getraenkeliste_${month?.replace(' ', '_') || 'Export'}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      
      setConfirmModal({
        isOpen: true,
        type: 'success',
        title: 'Export erfolgreich',
        message: 'Die PDF-Datei wurde erfolgreich heruntergeladen.',
        variant: 'single'
      });
    }
  };

  const handleClose = () => {
    setMonth('');
    setMembers([]);
    closePreview();
    onClose();
  };

  const handleAddColumn = async () => {
    if (!newColumnName || !newColumnPrice) {
      setConfirmModal({
        isOpen: true,
        type: 'error',
        title: 'Fehler',
        message: 'Bitte Name und Preis angeben.',
        variant: 'single'
      });
      return;
    }

    try {
      await apiClient.post('/api/drink-list-columns', {
        name: newColumnName,
        price: parseFloat(newColumnPrice),
        active: 1
      });
      setNewColumnName('');
      setNewColumnPrice('');
      await fetchColumns();
      await fetchAllColumns();
      setConfirmModal({
        isOpen: true,
        type: 'success',
        title: 'Erfolg',
        message: 'Spalte erfolgreich hinzugefügt.',
        variant: 'single'
      });
    } catch (err: any) {
      setConfirmModal({
        isOpen: true,
        type: 'error',
        title: 'Fehler',
        message: err.response?.data?.error || 'Spalte konnte nicht hinzugefügt werden.',
        variant: 'single'
      });
    }
  };

  const handleUpdateColumn = async (columnId: number, updates: Partial<DrinkColumn>, showModal: boolean = true) => {
    try {
      await apiClient.put(`/api/drink-list-columns/${columnId}`, updates);
      await fetchColumns();
      await fetchAllColumns();
      setEditingColumn(null);
      if (showModal) {
        setConfirmModal({
          isOpen: true,
          type: 'success',
          title: 'Erfolg',
          message: 'Spalte erfolgreich aktualisiert.',
          variant: 'single'
        });
      }
    } catch (err: any) {
      if (showModal) {
        setConfirmModal({
          isOpen: true,
          type: 'error',
          title: 'Fehler',
          message: err.response?.data?.error || 'Spalte konnte nicht aktualisiert werden.',
          variant: 'single'
        });
      }
    }
  };

  const handleDeleteColumn = async (columnId: number) => {
    try {
      await apiClient.delete(`/api/drink-list-columns/${columnId}`);
      await fetchColumns();
      await fetchAllColumns();
      setConfirmModal({
        isOpen: true,
        type: 'success',
        title: 'Erfolg',
        message: 'Spalte erfolgreich gelöscht.',
        variant: 'single'
      });
    } catch (err: any) {
      setConfirmModal({
        isOpen: true,
        type: 'error',
        title: 'Fehler',
        message: err.response?.data?.error || 'Spalte konnte nicht gelöscht werden.',
        variant: 'single'
      });
    }
  };

  const handleToggleActive = async (col: DrinkColumn) => {
    await handleUpdateColumn(col.id, { active: !col.active }, false); // Kein Modal anzeigen
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Modal für Export-Optionen */}
      <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50">
        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl w-full max-w-lg p-8 m-4">
          {/* Header */}
          <div className="flex justify-between items-center mb-6">
            <div className="flex items-center gap-3">
              <DocumentArrowDownIcon className="h-8 w-8 text-primary-600" />
              <h3 className="text-xl font-semibold text-gray-900 dark:text-white">
                Getränkeliste exportieren
              </h3>
            </div>
            <button
              onClick={handleClose}
              className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
              aria-label="Modal schließen"
            >
              <XMarkIcon className="h-5 w-5 text-gray-500 dark:text-gray-400" />
            </button>
          </div>

          {/* Monat Eingabe */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Monat / Zeitraum
            </label>
            <input
              type="text"
              value={month}
              onChange={(e) => setMonth(e.target.value)}
              placeholder="z.B. April 2025"
              className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg
                       bg-white dark:bg-gray-700 text-gray-900 dark:text-white
                       focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all"
            />
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
              Dieser Text erscheint auf dem exportierten Dokument
            </p>
          </div>

          {/* Spalten verwalten */}
          <div className="mb-6">
            <div className="flex items-center justify-between mb-2">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                Spaltenkonfiguration
              </label>
              <button
                onClick={() => setIsManageColumnsOpen(true)}
                className="text-sm text-primary-600 dark:text-primary-400 hover:underline"
              >
                Spalten verwalten
              </button>
            </div>
            <div className="flex flex-wrap gap-2">
              {allColumns.map((col) => (
                <button
                  key={col.id}
                  onClick={() => handleToggleActive(col)}
                  className={`px-3 py-1.5 rounded-full text-sm transition-all cursor-pointer flex items-center gap-1.5 ${
                    col.active
                      ? 'bg-success-100 text-success-700 dark:bg-success-900/30 dark:text-success-400 hover:bg-success-200 dark:hover:bg-success-900/50'
                      : 'bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-500 hover:bg-gray-200 dark:hover:bg-gray-600'
                  }`}
                  title={col.active ? 'Klicken zum Deaktivieren' : 'Klicken zum Aktivieren'}
                >
                  {col.active ? (
                    <CheckCircleIcon className="h-4 w-4" />
                  ) : (
                    <XCircleIcon className="h-4 w-4" />
                  )}
                  <span>{col.name} ({col.price.toFixed(2)} €)</span>
                </button>
              ))}
            </div>
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
              {columns.length} von {allColumns.length} Spalten aktiv. Klicke auf eine Spalte, um sie zu aktivieren/deaktivieren.
            </p>
          </div>

          {/* Gäste einschließen */}
          <div className="mb-6">
            <div className="flex items-center justify-between">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={includeGuests}
                  onChange={(e) => setIncludeGuests(e.target.checked)}
                  className="w-4 h-4 text-primary-600 bg-gray-100 border-gray-300 rounded
                           focus:ring-primary-500 focus:ring-2 dark:bg-gray-700 dark:border-gray-600"
                />
                <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  Gäste einschließen
                </span>
              </label>
              {guestCount > 0 && (
                <span className="text-xs text-gray-500 dark:text-gray-400">
                  {guestCount} {guestCount === 1 ? 'Gast' : 'Gäste'} verfügbar
                </span>
              )}
            </div>
          </div>

          {/* Info */}
          <div className="mb-6 p-4 bg-info-50 dark:bg-info-900/20 rounded-lg">
            <p className="text-sm text-info-800 dark:text-info-300">
              <strong>Hinweis:</strong> Es werden {filteredMembers.length} {includeGuests ? 'Personen' : 'aktive Mitglieder'} exportiert.
              {!includeGuests && guestCount > 0 && ` ${guestCount} ${guestCount === 1 ? 'Gast ist' : 'Gäste sind'} nicht enthalten.`}
            </p>
          </div>

          {/* Export Buttons */}
          <div className="flex flex-col sm:flex-row gap-3">
            <button
              onClick={handlePreview}
              disabled={loading}
              className="flex-1 px-4 py-2.5 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 
                       rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-all font-medium 
                       flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? (
                <div className="w-5 h-5 border-2 border-gray-500 border-t-transparent rounded-full animate-spin" />
              ) : (
                <>
                  <DocumentTextIcon className="h-5 w-5" />
                  Vorschau
                </>
              )}
            </button>
            
            <button
              onClick={() => generatePDF(false)}
              disabled={loading}
              className="flex-1 px-4 py-2.5 bg-primary-600 text-white rounded-lg hover:bg-primary-700 
                       transition-all font-medium flex items-center justify-center gap-2 
                       disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? (
                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
              ) : (
                <>
                  <DocumentArrowDownIcon className="h-5 w-5" />
                  PDF Export
                </>
              )}
            </button>
            
            <button
              onClick={generateExcel}
              disabled={loading}
              className="flex-1 px-4 py-2.5 bg-success-600 text-white rounded-lg hover:bg-success-700 
                       transition-all font-medium flex items-center justify-center gap-2 
                       disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? (
                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
              ) : (
                <>
                  <TableCellsIcon className="h-5 w-5" />
                  Excel Export
                </>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* PDF Vorschau Modal - wie DocumentPreviewModal */}
      {isPreviewOpen && (
        <>
          {/* Backdrop */}
          <div
            className="fixed inset-0 bg-black bg-opacity-75 z-[120]"
            onClick={closePreview}
          />
          
          {/* Modal */}
          <div className="fixed inset-0 z-[120] flex items-center justify-center p-4 pointer-events-none">
            <div className="bg-white dark:bg-gray-900 rounded-t-2xl sm:rounded-2xl shadow-2xl w-full max-w-4xl pointer-events-auto flex flex-col" 
                 style={{ height: '80vh', minHeight: '600px', maxHeight: '800px' }}>
              
              {/* Header */}
              <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
                <div>
                  <h2 className="text-xl font-bold text-gray-900 dark:text-white">
                    Getränkeliste - Vorschau
                  </h2>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    {month || 'Monat'} - {filteredMembers.length} Mitglieder
                  </p>
                </div>
                <button
                  onClick={closePreview}
                  className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors"
                >
                  <XMarkIcon className="h-6 w-6 text-gray-500" />
                </button>
              </div>
              
              {/* PDF Content */}
              <div className="flex-1 bg-gray-50 dark:bg-gray-800 p-4 overflow-auto">
                <div className="bg-white rounded-lg border border-gray-200 dark:border-gray-700 p-8 min-h-full">
                  {/* Simulierte PDF-Vorschau */}
                  <div className="max-w-2xl mx-auto">
                    {/* Header */}
                    <div className="bg-success-500 text-white p-4 rounded-t-lg mb-4">
                      <h3 className="text-lg font-bold">HSV Berlin-Adlershof e.V.</h3>
                      <p className="text-sm">Getränkeliste - {month || 'Monat'}</p>
                    </div>
                    
                    {/* Tabelle - dynamisch */}
                    <table className="w-full border-collapse text-sm">
                      <thead>
                        <tr className="bg-gray-100 border-b-2 border-gray-300">
                          <th className="text-left p-2 border border-gray-300">Name</th>
                          {columns.map((col) => (
                            <th key={col.id} className="text-center p-2 border border-gray-300">
                              {col.name}<br/>{col.price.toFixed(2)} €
                            </th>
                          ))}
                          <th className="text-center p-2 border border-gray-300">Gesamt</th>
                        </tr>
                      </thead>
                      <tbody>
                        {/* Reguläre Mitglieder */}
                        {regularMembers.slice(0, 15).map((member, index) => (
                          <tr key={member.mg_nr} className={index % 2 === 0 ? 'bg-gray-50' : 'bg-white'}>
                            <td className="p-2 border border-gray-300">
                              {member.vorname} {member.name}
                            </td>
                            {columns.map((col) => (
                              <td key={col.id} className="p-2 border border-gray-300"></td>
                            ))}
                            <td className="p-2 border border-gray-300"></td>
                          </tr>
                        ))}
                        {regularMembers.length > 15 && !includeGuests && (
                          <tr>
                            <td colSpan={columns.length + 2} className="p-2 text-center text-gray-500 italic">
                              ... und {regularMembers.length - 15} weitere Mitglieder
                            </td>
                          </tr>
                        )}

                        {/* Gäste-Bereich */}
                        {includeGuests && guestMembers.length > 0 && (
                          <>
                            <tr>
                              <td colSpan={columns.length + 2} className="p-3 bg-warning-500 text-white font-bold text-center border border-warning-600">
                                GÄSTE
                              </td>
                            </tr>
                            {guestMembers.slice(0, 5).map((member, index) => (
                              <tr key={member.mg_nr} className="bg-warning-50">
                                <td className="p-2 border border-gray-300">
                                  {member.vorname} {member.name}
                                </td>
                                {columns.map((col) => (
                                  <td key={col.id} className="p-2 border border-gray-300"></td>
                                ))}
                                <td className="p-2 border border-gray-300"></td>
                              </tr>
                            ))}
                            {guestMembers.length > 5 && (
                              <tr className="bg-warning-50">
                                <td colSpan={columns.length + 2} className="p-2 text-center text-gray-500 italic border border-gray-300">
                                  ... und {guestMembers.length - 5} weitere Gäste
                                </td>
                              </tr>
                            )}
                          </>
                        )}

                        {regularMembers.length > 15 && includeGuests && (
                          <tr>
                            <td colSpan={columns.length + 2} className="p-2 text-center text-gray-500 italic">
                              ... {regularMembers.length - 15 > 0 && `${regularMembers.length - 15} weitere Mitglieder`}
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                    
                    {/* Footer */}
                    <div className="mt-8 pt-4 border-t-2 border-success-500 text-center text-xs text-gray-600">
                      <p>Vielen Dank für Ihren Besuch! – HSV Berlin-Adlershof e.V.</p>
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Footer with Actions */}
              <div className="flex items-center justify-between p-4 border-t border-gray-200 dark:border-gray-700">
                <div className="text-sm text-gray-600 dark:text-gray-400">
                  PDF-Dokument bereit zum Download
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={closePreview}
                    className="px-4 py-2 text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-800 
                             hover:bg-gray-200 dark:hover:bg-gray-700 rounded-lg transition-colors text-sm font-medium"
                  >
                    Schließen
                  </button>
                  <button
                    onClick={() => {
                      downloadPdfFromPreview();
                      closePreview();
                    }}
                    className="px-4 py-2 bg-primary-600 text-white hover:bg-primary-700 
                             rounded-lg transition-colors text-sm font-medium flex items-center gap-2"
                  >
                    <DocumentArrowDownIcon className="h-4 w-4" />
                    PDF Herunterladen
                  </button>
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Confirmation Modal */}
      <ConfirmationModal
        visible={confirmModal.isOpen}
        type={confirmModal.type}
        title={confirmModal.title}
        message={confirmModal.message}
        variant={confirmModal.variant}
        button={{
          text: 'OK',
          onPress: () => setConfirmModal({ ...confirmModal, isOpen: false })
        }}
      />

      {/* Manage Columns Modal */}
      {isManageColumnsOpen && (
        <>
          <div
            className="fixed inset-0 bg-black bg-opacity-60 z-[60]"
            onClick={() => setIsManageColumnsOpen(false)}
          />
          <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 pointer-events-none">
            <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl w-full max-w-2xl pointer-events-auto max-h-[80vh] overflow-auto">
              {/* Header */}
              <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
                <h3 className="text-xl font-semibold text-gray-900 dark:text-white">
                  Spalten verwalten
                </h3>
                <button
                  onClick={() => setIsManageColumnsOpen(false)}
                  className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                >
                  <XMarkIcon className="h-5 w-5 text-gray-500 dark:text-gray-400" />
                </button>
              </div>

              {/* Content */}
              <div className="p-6">
                {/* Neue Spalte hinzufügen */}
                <div className="mb-6 p-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                  <h4 className="text-sm font-semibold text-gray-900 dark:text-white mb-3">
                    Neue Spalte hinzufügen
                  </h4>
                  <div className="flex gap-3">
                    <input
                      type="text"
                      value={newColumnName}
                      onChange={(e) => setNewColumnName(e.target.value)}
                      placeholder="Name (z.B. Kaffee)"
                      className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg
                               bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm
                               focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    />
                    <input
                      type="number"
                      step="0.01"
                      value={newColumnPrice}
                      onChange={(e) => setNewColumnPrice(e.target.value)}
                      placeholder="Preis (€)"
                      className="w-28 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg
                               bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm
                               focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    />
                    <button
                      onClick={handleAddColumn}
                      className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700
                               transition-all text-sm font-medium"
                    >
                      Hinzufügen
                    </button>
                  </div>
                </div>

                {/* Vorhandene Spalten */}
                <div>
                  <h4 className="text-sm font-semibold text-gray-900 dark:text-white mb-3">
                    Vorhandene Spalten
                  </h4>
                  <div className="space-y-2">
                    {allColumns.map((col) => (
                      <div
                        key={col.id}
                        className="flex items-center gap-3 p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg"
                      >
                        {editingColumn?.id === col.id ? (
                          <>
                            <input
                              type="text"
                              defaultValue={col.name}
                              onChange={(e) => setEditingColumn({ ...editingColumn, name: e.target.value })}
                              className="flex-1 px-3 py-1.5 border border-gray-300 dark:border-gray-600 rounded
                                       bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
                            />
                            <input
                              type="number"
                              step="0.01"
                              defaultValue={col.price}
                              onChange={(e) => setEditingColumn({ ...editingColumn, price: parseFloat(e.target.value) })}
                              className="w-24 px-3 py-1.5 border border-gray-300 dark:border-gray-600 rounded
                                       bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
                            />
                            <button
                              onClick={() => handleUpdateColumn(col.id, { name: editingColumn.name, price: editingColumn.price })}
                              className="px-3 py-1.5 bg-success-600 text-white rounded text-sm hover:bg-success-700"
                            >
                              Speichern
                            </button>
                            <button
                              onClick={() => setEditingColumn(null)}
                              className="px-3 py-1.5 bg-gray-500 text-white rounded text-sm hover:bg-gray-600"
                            >
                              Abbrechen
                            </button>
                          </>
                        ) : (
                          <>
                            <div className="flex-1">
                              <div className="text-sm font-medium text-gray-900 dark:text-white">
                                {col.name}
                              </div>
                              <div className="text-xs text-gray-500 dark:text-gray-400">
                                {col.price.toFixed(2)} €
                              </div>
                            </div>
                            <button
                              onClick={() => handleToggleActive(col)}
                              className={`px-3 py-1.5 rounded text-xs font-medium transition-colors ${
                                col.active
                                  ? 'bg-success-100 text-success-700 dark:bg-success-900/30 dark:text-success-400'
                                  : 'bg-gray-200 text-gray-700 dark:bg-gray-700 dark:text-gray-400'
                              }`}
                            >
                              {col.active ? 'Aktiv' : 'Inaktiv'}
                            </button>
                            <button
                              onClick={() => setEditingColumn(col)}
                              className="px-3 py-1.5 bg-info-600 text-white rounded text-sm hover:bg-info-700"
                            >
                              Bearbeiten
                            </button>
                            <button
                              onClick={() => handleDeleteColumn(col.id)}
                              className="px-3 py-1.5 bg-error-600 text-white rounded text-sm hover:bg-error-700"
                            >
                              Löschen
                            </button>
                          </>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Footer */}
              <div className="flex justify-end p-6 border-t border-gray-200 dark:border-gray-700">
                <button
                  onClick={() => setIsManageColumnsOpen(false)}
                  className="px-4 py-2 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300
                           rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                >
                  Schließen
                </button>
              </div>
            </div>
          </div>
        </>
      )}
    </>
  );
};

export default DrinkListExport;